import { IApiSettingsResponse, IRawCompanySettingsGeneral, TApiSettingsResponse, TRawCompanySettingsGeneral } from "@propely/propely-typings";
import { queryClient } from "@src/index";
import { getDoc, getFirestore, doc, setDoc, updateDoc } from "firebase/firestore";
import { useQuery } from "react-query";
import { useRealtimeDocumentData } from "../generic/useRealtimeDocumentData";
import { propelyApi } from "@src/libs/firebase/api";

type ApiSettingsFilter = {
  propertyID?: string;
};

export const apiSettingsQueryKey = (filters?: ApiSettingsFilter) => ["get-api-settings", filters];

export const fetchAPISettings = async (filters?: ApiSettingsFilter) => {
  const response = await propelyApi().get("/settings", { params: filters });
  return TApiSettingsResponse.parse(response.data);
};

export const useAPISettings = (filters?: ApiSettingsFilter) => {
  const $settings = useQuery(apiSettingsQueryKey(filters), () => fetchAPISettings(filters));
  const settings = $settings.data;
  return { $settings, settings };
};

/**
 * @deprecated use useAPISettings instead
 */
export const getCachedApiSettings = (filters?: ApiSettingsFilter): IApiSettingsResponse | undefined => {
  const response = queryClient.getQueryData(apiSettingsQueryKey(filters));
  const parsedResponse = TApiSettingsResponse.safeParse(response);
  if (parsedResponse.success) {
    return parsedResponse.data;
  } else if (filters) {
    return getCachedApiSettings();
  }
  return undefined;
};

// PROPERTY GENERAL SETTINGS

export const usePropertyGeneralSettingsKey = (propertyID: string) => ["property-general-settings", propertyID];
export const fetchPropertyGeneralSettings = async (propertyID: string): Promise<IRawCompanySettingsGeneral> => {
  const response = await getDoc(doc(getFirestore(), "properties", propertyID, "settings", "general"));
  const parsedData = TRawCompanySettingsGeneral.safeParse({
    id: response.id,
    ...response.data(),
  });
  return parsedData.success ? parsedData.data : {};
};

export const usePropertyGeneralSettings = (propertyID: string) => {
  const $settings = useQuery(usePropertyGeneralSettingsKey(propertyID), () => fetchPropertyGeneralSettings(propertyID));
  return {
    $settings,
    settings: $settings.data,
  };
};

export const setPropertyGeneralSettings = async (propertyID: string, userID: string, settings: Partial<IRawCompanySettingsGeneral>) => {
  await setDoc(
    doc(getFirestore(), "properties", propertyID, "settings", "general"),
    {
      ...settings,
      updatedBy: {
        userID,
        date: new Date(),
      },
    },
    { merge: true }
  );
  await queryClient.invalidateQueries(usePropertyGeneralSettingsKey(propertyID));
};

// COMPANY GENERAL SETTINGS
export const useCompanyGeneralSettingsKey = (companyID: string) => ["company-general-settings", companyID];
export const fetchCompanyGeneralSettings = async (companyID: string): Promise<IRawCompanySettingsGeneral> => {
  const response = await getDoc(doc(getFirestore(), "companies", companyID, "settings", "general"));
  const parsedData = TRawCompanySettingsGeneral.safeParse({
    id: response.id,
    ...response.data(),
  });
  return parsedData.success ? parsedData.data : {};
};

export const useCompanyGeneralSettings = (companyID: string) => {
  const $settings = useQuery(useCompanyGeneralSettingsKey(companyID), () => fetchCompanyGeneralSettings(companyID));
  return {
    $settings,
    settings: $settings.data,
  };
};

export const useRealtimeCompanyGeneralSettings = (companyID: string) => {
  return useRealtimeDocumentData(`companies/${companyID}/settings`, TRawCompanySettingsGeneral, "general");
};

export const setCompanyGeneralSettings = async (
  companyID: string,
  userID: string,
  settings: Partial<IRawCompanySettingsGeneral>,
  options?: { disableQueryInvalidation: boolean }
) => {
  await setDoc(
    doc(getFirestore(), "companies", companyID, "settings", "general"),
    {
      ...settings,
      updatedBy: {
        userID,
        date: new Date(),
      },
    },
    { merge: true }
  );
  await queryClient.invalidateQueries(useCompanyGeneralSettingsKey(companyID));

  // Wait some seconds before invalidating the cache for all property settings and api settings
  if (!options?.disableQueryInvalidation) {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    queryClient.invalidateQueries("get-api-settings");
    queryClient.invalidateQueries("property-general-settings");
  }
};

export const updateCompanyGeneralSettings = async (
  companyID: string,
  userID: string,
  settings: Partial<IRawCompanySettingsGeneral>,
  options?: {
    disableQueryInvalidation: boolean;
  }
) => {
  await updateDoc(doc(getFirestore(), "companies", companyID, "settings", "general"), {
    ...settings,
    updatedBy: {
      userID,
      date: new Date(),
    },
  });
  await queryClient.invalidateQueries(useCompanyGeneralSettingsKey(companyID));
  // Wait some seconds before invalidating the cache for all property settings and api settings
  if (!options?.disableQueryInvalidation) {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    queryClient.invalidateQueries("get-api-settings");
    queryClient.invalidateQueries("property-general-settings");
  }
};

